import { useCallback, useMemo } from 'react';
import { useNavigate, useParams, type NavigateOptions } from 'react-router-dom';
import { withWorkspace, type WorkspaceRoutes } from '@immo/api-contract';

/**
 * The active workspace lives in the URL as the first path segment
 * (`/w/:workspaceId/…`) so every in-app link is self-describing and deep-linkable
 * — a link to a resource carries the workspace it belongs to instead of relying
 * on ambient client state (IMM-322). This module is the single place that knows
 * the `/w/<id>` shape; everything else builds links through it.
 */

const WORKSPACE_SEGMENT = 'w';

/**
 * Build an absolute in-app URL for `appPath` (e.g. `/acquisition-pipeline/42`)
 * scoped to `workspaceId`. `appPath` is the workspace-relative path as it appears
 * in the route table; `/` maps to the workspace root (the dashboard).
 */
export function workspacePath(workspaceId: string, appPath = '/'): string {
  const suffix = appPath === '/' || appPath === '' ? '' : appPath.startsWith('/') ? appPath : `/${appPath}`;

  return `/${WORKSPACE_SEGMENT}/${workspaceId}${suffix}`;
}

/**
 * If the `/w/:segment` in `request`'s URL isn't the canonical workspace slug
 * (e.g. a renamed workspace's stale prefix, or a raw UUID), return the canonical
 * URL — same sub-path and query, segment swapped — for the caller to redirect to.
 * Returns `null` when already canonical (no redirect needed).
 */
export function canonicalWorkspaceRedirect(
  request: Request,
  currentSegment: string | undefined,
  canonicalSlug: string,
): string | null {
  if (currentSegment === undefined || currentSegment === canonicalSlug) return null;

  const url = new URL(request.url);
  const prefix = `/${WORKSPACE_SEGMENT}/${currentSegment}`;
  const rest = url.pathname.startsWith(prefix) ? url.pathname.slice(prefix.length) : '';

  return `${workspacePath(canonicalSlug)}${rest}${url.search}`;
}

/**
 * The active workspace id from the current route. Only valid inside the
 * `/w/:workspaceId` subtree; returns `undefined` elsewhere (callers there should
 * not be building workspace links).
 */
export function useWorkspaceId(): string | undefined {
  return useParams().workspaceId;
}

/**
 * The route catalog bound to the active workspace — `routes.contact(id)` →
 * `/w/<active>/contacts/<id>`. This is the primary way to build in-app links:
 * the `/w/:slug` prefix is baked into every builder, so a bare path is
 * impossible. Only valid inside the `/w/:workspaceId` subtree. For non-catalog
 * workspace paths (e.g. onboarding) use `useWorkspaceHref` / `useWorkspaceNavigate`.
 */
export function useWorkspaceRoutes(): WorkspaceRoutes {
  const workspaceId = useWorkspaceId();

  return useMemo(() => withWorkspace(workspaceId ?? ''), [workspaceId]);
}

/**
 * A link builder bound to the current route's workspace — `buildHref('/leads/owners')`
 * → `/w/<active>/leads/owners`. For catalog routes prefer `useWorkspaceRoutes`;
 * use this for workspace-relative paths that aren't in the catalog (onboarding).
 */
export function useWorkspaceHref(): (appPath: string) => string {
  const workspaceId = useWorkspaceId();

  return useCallback(
    (appPath: string) => (workspaceId === undefined ? appPath : workspacePath(workspaceId, appPath)),
    [workspaceId],
  );
}

/**
 * `navigate` bound to the current workspace: `go('/leads/owners')` navigates to
 * `/w/<active>/leads/owners`. For auth/onboarding paths that live outside the
 * workspace subtree, use the plain `useNavigate` instead.
 */
export function useWorkspaceNavigate(): (appPath: string, options?: NavigateOptions) => void {
  const navigate = useNavigate();
  const workspaceId = useWorkspaceId();

  return useCallback(
    (appPath: string, options?: NavigateOptions) => {
      void navigate(workspaceId === undefined ? appPath : workspacePath(workspaceId, appPath), options);
    },
    [navigate, workspaceId],
  );
}
